// This hook is lifted from here: https://www.npmjs.com/package/react-clear-cache
// There were a few problems w/ it's current published version that we fixed here
// The createPersistedState was created inside the hook, which caused the initial setting
// of the version to happen incorrectly
// The onComplete for fetching the meta.json was a function that referenced state
// So once the state updated, the function would fire with the old state values
// And emptyCacheStorage would set the latest version to an event when the button was clicked

import * as React from 'react';

const defaultProps = {
  duration: 60 * 1000,
  auto: false,
  basePath: '',
};

export const useClearCache = props => {
  const { duration, auto } = {
    ...defaultProps,
    ...props,
  };
  const [loading, setLoading] = React.useState(true);

  let appVersion;
  let setAppVersion;
  try {
    [appVersion, setAppVersion] = React.useState('75112277-cb3a-4895-9dce-223090cedf87');
  } catch (e) {
    try {
      window.Rollbar && window.Rollbar.warn('Error parsing app version in useClearCache');
    } catch (err) {
      // swallow logging if it fails
    }

    // If it fails to get the version into state, return everything like its all good to consumer
    return {
      loading: false,
      isLatestVersion: true,
      emptyCacheStorage: () => {},
      latestVersion: '',
    };
  }

  const [isLatestVersion, setIsLatestVersion] = React.useState(true);
  const [latestVersion, setLatestVersion] = React.useState(appVersion);

  async function setVersion(version) {
    await setAppVersion(version);
  }

  const emptyCacheStorage = async () => {
    if ('caches' in window) {
      // Service worker cache should be cleared with caches.delete()
      caches.keys().then(names => {
        // eslint-disable-next-line no-restricted-syntax
        for (const name of names) caches.delete(name);
      });
    }

    // after cache has been cleared, reload the page
    window.location.reload(true);
  };

  const versionHashRegExp = /<!-- VERSION: (\S+) -->/;
  const parseVersionHashFromHTML = htmlStr => htmlStr.match(versionHashRegExp)[1];

  const fetchMeta = React.useCallback(() => {
    fetch('/', {
      cache: 'no-store',
    })
      .then(response => response.text())
      .then(meta => {
        const newVersion = parseVersionHashFromHTML(meta);
        const currentVersion = appVersion;
        const sameVersion = newVersion === currentVersion;
        if (!sameVersion && !auto) {
          setLatestVersion(newVersion);
          setLoading(false);
          if (appVersion) {
            setIsLatestVersion(false);
          } else {
            setVersion(newVersion);
          }
        } else if (!sameVersion && auto) {
          emptyCacheStorage(newVersion);
        } else {
          setIsLatestVersion(true);
          setLoading(false);
        }
      })
      .catch(err => {
        // This rollbar event was initially put it to ensure that this feature was working and not failing
        // Now that we know it is, there are still a few events generated here - mostly all just client network errors
        // These are not systemic, and no longer warrant a WARN event
        // https://rollbar.com/Sunrun/customer-portal/items/1773/?item_page=0&#instances
        // window.Rollbar.warn('Failed to load meta.json', {
        //   message: err.message,
        // });
      });
  }, [appVersion, latestVersion]);

  React.useEffect(() => {
    const fetchCacheTimeout = setInterval(() => fetchMeta(), duration);
    return () => {
      clearInterval(fetchCacheTimeout);
    };
  }, [loading]);

  React.useEffect(() => {
    fetchMeta();
  }, []);

  return {
    loading,
    isLatestVersion,
    emptyCacheStorage,
    latestVersion,
  };
};
